import { Box, Stack, Typography } from '@mui/material';
import type { AgBarSeriesOptions } from 'ag-charts-enterprise';
import dayjs from 'dayjs';
import { useAtom } from 'jotai';
import { useMemo } from 'react';

import VerticalBarChart from '@/components/charts/VerticalBarChart';
import { REPORTS_COLORS } from '@/constants/chartcolors.constants';
import REPORT_PARAGRAPHS from '@/constants/reportparagraph.constants';
import ReportsTemplateBoxContainer from '@/features/ReportsTemplateBoxContainer/ReportsTemplateBoxContainer';
import ReportFullWidthContainer from '@/features/ReportsTemplateFullWidthContainer/ReportsFullWidthContainer';
import ReportParagraph from '@/features/ReportsTemplateParagraph/ReportsParagraph';
import ReportsTemplateStatBox from '@/features/ReportsTemplateStatBox/ReportsTemplateStatBox';
import { formatLargeNumber } from '@/utils/Common.utils';

import {
  reportsEndDateAtom,
  reportsTimeFrameAtom
} from '../../ReportsTemplate.hooks';
import {
  getCountsByDate,
  getURLTimeframeData
} from '../../ReportsTemplate.utils';
import type { Event1Props } from '../ReportsPage.types';
import useStyles from '../ReportsPages.styles';

const EventsPage1 = ({
  eventsDataRaw,
  hasLog,
  hasNetwork,
  hasEDR
}: Event1Props) => {
  const { classes } = useStyles();
  const [diff] = useAtom(reportsTimeFrameAtom);
  const [endDate] = useAtom(reportsEndDateAtom);
  const minTimeFrame = diff < 7 ? 7 : diff;

  const singleDayEvents = getURLTimeframeData(eventsDataRaw, 'date');

  const timeFrameCorrectedEvents = diff < 7 ? singleDayEvents : eventsDataRaw;

  // Total Log Events
  const totalLogEvents = useMemo(() => {
    return timeFrameCorrectedEvents
      .filter((item) => item.type === 'log')
      .reduce((sum, curr) => sum + (curr.count || 0), 0);
  }, [timeFrameCorrectedEvents]);

  // Total Network Events
  const totalNetEvents = useMemo(() => {
    return timeFrameCorrectedEvents
      .filter((item) => item.type === 'net')
      .reduce((sum, curr) => sum + (curr.count || 0), 0);
  }, [timeFrameCorrectedEvents]);

  // Grand Total Events
  const totalEvents = useMemo(
    () => totalLogEvents + totalNetEvents,
    [totalLogEvents, totalNetEvents]
  );

  const avgEventsPerDay = useMemo(() => {
    if (!diff) return 0;
    return Math.round(totalEvents / diff);
  }, [totalEvents, diff]);

  const graphData = useMemo(() => {
    const finalDataSet: {
      [key: string]: { date: Date; logCount: number; networkCount: number };
    } = {};

    const logEventsCountsByDate = hasLog
      ? getCountsByDate(
          minTimeFrame,
          eventsDataRaw.filter((event) => event.type === 'log'),
          endDate
        )
      : {};

    const networkEventsCountsByDate = hasNetwork
      ? getCountsByDate(
          minTimeFrame,
          eventsDataRaw.filter((event) => event.type === 'net'),
          endDate
        )
      : {};

    const allDates = new Set([
      ...Object.keys(logEventsCountsByDate),
      ...Object.keys(networkEventsCountsByDate)
    ]);

    allDates.forEach((dateKey) => {
      finalDataSet[dateKey] = {
        date: dayjs(dateKey).toDate(),
        logCount: logEventsCountsByDate[dateKey] || 0,
        networkCount: networkEventsCountsByDate[dateKey] || 0
      };
    });

    return Object.values(finalDataSet).sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );
  }, [timeFrameCorrectedEvents, minTimeFrame, hasLog, hasNetwork, endDate]);

  const eventsVerticalBarSeries: AgBarSeriesOptions[] = [];

  if (hasLog) {
    eventsVerticalBarSeries.push({
      type: 'bar',
      xKey: 'date',
      yKey: 'logCount',
      yName: 'Log Events ',
      fill: REPORTS_COLORS.LOG_EVENTS
    });
  }

  if (hasNetwork) {
    eventsVerticalBarSeries.push({
      type: 'bar',
      xKey: 'date',
      yKey: 'networkCount',
      yName: 'Network Events',
      fill: REPORTS_COLORS.NETWORK_EVENTS
    });
  }

  const eventsGraph = useMemo(() => {
    return (
      <>
        <Typography className={classes.reportsPreviewSubTitle}>
          Total Events Per Day
        </Typography>
        <VerticalBarChart
          data={graphData}
          series={eventsVerticalBarSeries}
          outlined
          doubleAxis={hasLog && hasNetwork}
        />
      </>
    );
  }, [graphData, minTimeFrame, hasLog, hasNetwork]);

  return (
    <div className={classes.reportsPageContainer}>
      <ReportParagraph>{REPORT_PARAGRAPHS.eventsDescription}</ReportParagraph>
      <ReportFullWidthContainer title="Total Events">
        <div className={classes.totalEventsText}>
          {formatLargeNumber(totalEvents, true)}
        </div>
      </ReportFullWidthContainer>
      <Box sx={{ flexDirection: 'row', display: 'flex', gap: 2 }}>
        {hasLog && (
          <ReportsTemplateBoxContainer>
            <ReportsTemplateStatBox
              title="Total Number of Log Events"
              value={formatLargeNumber(totalLogEvents, true)}
            />
          </ReportsTemplateBoxContainer>
        )}
        {hasNetwork && (
          <ReportsTemplateBoxContainer>
            <ReportsTemplateStatBox
              title="Total Number of Network Events"
              value={formatLargeNumber(totalNetEvents, true)}
            />
          </ReportsTemplateBoxContainer>
        )}
        {/* For EDR-only customers, show log events as EDR events */}
        {hasEDR && !hasLog && (
          <ReportsTemplateBoxContainer>
            <ReportsTemplateStatBox
              title="Total Number of EDR Events"
              value={formatLargeNumber(totalLogEvents, true)}
            />
          </ReportsTemplateBoxContainer>
        )}
        <ReportsTemplateBoxContainer>
          <ReportsTemplateStatBox
            title="Avg. Events Per Day"
            value={formatLargeNumber(avgEventsPerDay, true)}
          />
        </ReportsTemplateBoxContainer>
      </Box>
      <Stack>
        <div
          style={{ width: '100%', height: '300px' }}
          className={classes.reportsChartContainer}
          data-testid="events-bar-graph"
        >
          {eventsGraph}
        </div>
        <ReportParagraph fontSize="12px">
          {REPORT_PARAGRAPHS.eventsPerDayDescription}
        </ReportParagraph>
      </Stack>
    </div>
  );
};

export default EventsPage1;
